import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Buildings, MagnifyingGlass, Phone, Envelope, WhatsappLogo,
  Package, ArrowRight, CurrencyInr, CaretDown, CaretRight, Warning,
} from "@phosphor-icons/react";

const STAGE_LABEL = {
  new_order: "New Order",
  framing_making: "Frame Ready",
  design_making: "Design Install",
  design_cutting: "Cutting Done",
  completed: "Ready for Delivery",
  delivered: "Delivered",
};

/**
 * FabricationOverviewPage — admin view of "who has which order".
 * Groups all gate/railing orders by fabricator, plus an "Unassigned"
 * bucket for gate orders that don't have a fabricator yet.
 */
export default function FabricationOverviewPage({ onOpenOrder }) {
  const [fabricators, setFabricators] = useState([]);
  const [ordersByFab, setOrdersByFab] = useState({});
  const [unassigned, setUnassigned] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [showDelivered, setShowDelivered] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: fabs } = await api.get("/fabricators");
      setFabricators(fabs || []);
      // Fetch orders per fabricator in parallel
      const results = await Promise.all(
        (fabs || []).map((f) =>
          api.get("/orders", { params: { fabricator_id: f.id, project_type: "gate_railing", limit: 500 } })
            .then((r) => [f.id, r.data || []])
            .catch(() => [f.id, []])
        )
      );
      const map = {};
      results.forEach(([fid, orders]) => { map[fid] = orders; });
      setOrdersByFab(map);
      // Auto-expand first fabricator on initial load
      if ((fabs || []).length > 0 && Object.keys(expanded).length === 0) {
        setExpanded({ [fabs[0].id]: true });
      }
      // Unassigned gate orders
      const { data: un } = await api.get("/orders", {
        params: { fabricator_id: "none", project_type: "gate_railing", limit: 500 },
      });
      setUnassigned(un || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const searchFilter = (o) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (o.order_number || "").toLowerCase().includes(s)
      || (o.customer_name || "").toLowerCase().includes(s)
      || (o.mobile || "").includes(s);
  };
  const stageFilter = (o) => showDelivered ? true : o.status !== "delivered";

  // Filtered totals for header pills
  const totals = useMemo(() => {
    let openOrders = 0;
    let overdue = 0;
    let totalAdv = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    fabricators.forEach((f) => {
      totalAdv += f.advance_total || 0;
      openOrders += f.open_orders_count || 0;
      (ordersByFab[f.id] || []).forEach((o) => {
        if (o.status === "delivered") return;
        if (o.deadline_date) {
          const d = new Date(o.deadline_date);
          if (!Number.isNaN(d.getTime()) && d.getTime() < today.getTime()) overdue += 1;
        }
      });
    });
    return { openOrders, overdue, totalAdv, fabs: fabricators.length, unassigned: unassigned.filter((o) => o.status !== "delivered").length };
  }, [fabricators, ordersByFab, unassigned]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4" data-testid="fabrication-overview-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// FABRICATION</div>
          <h1 className="font-heading text-3xl font-black text-white flex items-center gap-2">
            <Buildings size={26} className="text-orange-400" weight="fill" /> Fabrication Department
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Har fabricator ke paas kaunse gate/railing orders hain, aur kitna advance mila hai — ek jagah track karo.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <MetricPill label="Fabricators" value={totals.fabs} tone="cyan" />
          <MetricPill label="Open Orders" value={totals.openOrders} tone="orange" />
          <MetricPill label="Unassigned" value={totals.unassigned} tone={totals.unassigned > 0 ? "red" : "slate"} />
          <MetricPill label="Overdue" value={totals.overdue} tone={totals.overdue > 0 ? "red" : "slate"} />
          <MetricPill label="Advance Paid" value={fmt(totals.totalAdv)} tone="amber" />
        </div>
      </div>

      {/* Search + filter */}
      <div className="surface p-3 rounded-sm flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search order # / customer / mobile…"
            className="pl-8 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="fab-overview-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs font-mono-num tracking-widest text-slate-400 cursor-pointer">
          <input
            type="checkbox" checked={showDelivered}
            onChange={(e) => setShowDelivered(e.target.checked)}
            className="accent-orange-500"
            data-testid="fab-show-delivered"
          />
          SHOW DELIVERED
        </label>
      </div>

      {busy && fabricators.length === 0 ? (
        <div className="surface p-8 rounded-sm text-center text-slate-500">Loading fabrication data…</div>
      ) : fabricators.length === 0 && unassigned.length === 0 ? (
        <div className="surface p-8 rounded-sm text-center text-slate-500">
          Koi fabricator nahi mila. <b>Settings → Users</b> me jaake role <b>"Fabrication"</b> se user add karo.
        </div>
      ) : (
        <div className="space-y-3">
          {/* Unassigned bucket — shown on top only if there are unassigned orders */}
          {unassigned.filter(searchFilter).filter(stageFilter).length > 0 && (
            <UnassignedGroup
              orders={unassigned.filter(searchFilter).filter(stageFilter)}
              expanded={expanded.__unassigned}
              onToggle={() => setExpanded((e) => ({ ...e, __unassigned: !e.__unassigned }))}
              onOpenOrder={onOpenOrder}
            />
          )}

          {fabricators.map((f) => {
            const orders = (ordersByFab[f.id] || []).filter(searchFilter).filter(stageFilter);
            const openCnt = orders.filter((o) => o.status !== "delivered").length;
            return (
              <FabricatorGroup
                key={f.id}
                fab={f}
                orders={orders}
                openCount={openCnt}
                fmt={fmt}
                expanded={expanded[f.id]}
                onToggle={() => setExpanded((e) => ({ ...e, [f.id]: !e[f.id] }))}
                onOpenOrder={onOpenOrder}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function MetricPill({ label, value, tone }) {
  const toneCls = {
    cyan: "text-cyan-300", emerald: "text-emerald-400", amber: "text-amber-300",
    orange: "text-orange-400", red: "text-red-300", slate: "text-slate-200",
  }[tone] || "text-slate-100";
  return (
    <div className="surface p-3 rounded-sm min-w-[110px]">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label.toUpperCase()}</div>
      <div className={"font-mono-num font-black text-xl " + toneCls}>{value}</div>
    </div>
  );
}

function FabricatorGroup({ fab, orders, openCount, fmt, expanded, onToggle, onOpenOrder }) {
  return (
    <div className="surface rounded-sm overflow-hidden" data-testid={`fab-group-${fab.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40 transition-colors text-left"
      >
        {expanded ? <CaretDown size={16} className="text-slate-400" /> : <CaretRight size={16} className="text-slate-400" />}
        <div className="w-9 h-9 rounded-sm bg-orange-500/15 border border-orange-500/50 flex items-center justify-center shrink-0">
          <Buildings size={16} className="text-orange-400" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-slate-100 truncate">{fab.name}</div>
          <div className="text-slate-400 text-xs font-mono-num flex flex-wrap items-center gap-x-2">
            {fab.email && <span className="flex items-center gap-1"><Envelope size={10} /> {fab.email}</span>}
            {fab.mobile && <span className="flex items-center gap-1 text-cyan-400"><Phone size={10} /> {fab.mobile}</span>}
          </div>
        </div>
        <div className="hidden sm:flex gap-2 items-center">
          <SmallStat label="OPEN" value={openCount} tone={openCount > 0 ? "orange" : "slate"} />
          <SmallStat label="TOTAL" value={fab.total_orders || 0} />
          <SmallStat label="ADV PAID" value={fmt(fab.advance_total)} tone="amber" wide />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-800">
          {orders.length === 0 ? (
            <div className="px-6 py-6 text-center text-slate-500 text-sm">
              Is fabricator ke paas koi {(fab.total_orders || 0) === 0 ? "" : "matching "}order nahi.
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {orders.map((o) => <OrderRow key={o.id} order={o} onOpen={() => onOpenOrder?.(o.id)} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UnassignedGroup({ orders, expanded, onToggle, onOpenOrder }) {
  return (
    <div className="surface rounded-sm overflow-hidden border-l-4 border-red-500/60" data-testid="fab-group-unassigned">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40 transition-colors text-left"
      >
        {expanded ? <CaretDown size={16} className="text-red-300" /> : <CaretRight size={16} className="text-red-300" />}
        <div className="w-9 h-9 rounded-sm bg-red-500/15 border border-red-500/50 flex items-center justify-center shrink-0">
          <Warning size={16} className="text-red-300" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-red-200">Unassigned Orders</div>
          <div className="text-slate-400 text-xs">Gate/railing orders bina fabricator ke — turant assign karo</div>
        </div>
        <SmallStat label="PENDING" value={orders.length} tone="red" />
      </button>
      {expanded && (
        <div className="border-t border-slate-800 divide-y divide-slate-800">
          {orders.map((o) => <OrderRow key={o.id} order={o} onOpen={() => onOpenOrder?.(o.id)} />)}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order, onOpen }) {
  const stageLabel = STAGE_LABEL[order.status] || order.status;
  const isDelivered = order.status === "delivered";
  const daysAgo = order.created_at ? Math.floor((Date.now() - new Date(order.created_at).getTime()) / 86400000) : null;
  const deadlineInfo = order.deadline_date ? (() => {
    const d = new Date(order.deadline_date);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const t1 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diff = Math.round((t1 - t2) / 86400000);
    return { diff, label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) };
  })() : null;

  const paid = Number(order.advance_paid || 0);
  const total = Number(order.total_amount || 0);
  const balance = Math.max(0, total - paid);

  return (
    <button
      onClick={onOpen}
      className="w-full px-4 py-3 hover:bg-slate-800/40 transition-colors text-left flex items-start gap-3"
      data-testid={`fab-order-row-${order.order_number}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-num text-orange-400 font-bold">#{order.order_number}</span>
          <span className="text-slate-100 truncate">{order.customer_name}</span>
          <span className="text-slate-500 font-mono-num text-xs">{order.mobile}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className={
            "text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm font-bold " +
            (isDelivered ? "bg-emerald-500/15 border border-emerald-500/50 text-emerald-300"
              : "bg-orange-500/15 border border-orange-500/50 text-orange-300")
          }>
            {stageLabel.toUpperCase()}
          </span>
          {!isDelivered && daysAgo !== null && daysAgo >= 3 && (
            <span className="text-[9px] font-mono-num tracking-widest bg-red-500/20 border border-red-500/60 text-red-200 px-1.5 py-0.5 rounded-sm font-bold animate-pulse">
              {daysAgo} DIN
            </span>
          )}
          {!isDelivered && deadlineInfo && (
            <span className={
              "text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm font-bold " +
              (deadlineInfo.diff < 0
                ? "bg-red-500/30 border border-red-500/70 text-red-100 animate-pulse"
                : deadlineInfo.diff === 0
                  ? "bg-red-500/25 border border-red-500/70 text-red-100 animate-pulse"
                  : deadlineInfo.diff <= 2
                    ? "bg-amber-500/25 border border-amber-500/70 text-amber-100"
                    : "bg-cyan-500/15 border border-cyan-500/50 text-cyan-200")
            }>
              🔒 {deadlineInfo.diff < 0
                ? `OVERDUE ${Math.abs(deadlineInfo.diff)}D`
                : deadlineInfo.diff === 0
                  ? "DUE TODAY"
                  : deadlineInfo.diff <= 2
                    ? `DUE ${deadlineInfo.diff}D`
                    : `DUE ${deadlineInfo.label}`}
            </span>
          )}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="text-slate-300 font-mono-num text-sm">₹{total.toLocaleString("en-IN")}</div>
        {balance > 0 && !isDelivered && (
          <div className="text-red-300 font-mono-num text-xs">DUE ₹{balance.toLocaleString("en-IN")}</div>
        )}
      </div>
      <ArrowRight size={14} className="text-slate-500 mt-1 shrink-0" />
    </button>
  );
}

function SmallStat({ label, value, tone = "slate", wide = false }) {
  const toneCls = {
    orange: "text-orange-400", red: "text-red-300", amber: "text-amber-300", slate: "text-slate-300",
  }[tone];
  return (
    <div className="bg-slate-800/50 rounded-sm px-2 py-1">
      <div className="text-[8px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono-num font-bold ${wide ? "text-xs" : "text-sm"} ${toneCls}`}>{value}</div>
    </div>
  );
}
